import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient } from '../clients.js';
import {
  BuildRelatedFieldsMapRequestSchema,
  BuildRelatedFieldsMapResponseSchema,
  CheckFieldValueUniquenessRequestSchema,
  CheckUniquenessFieldDefinitionQuerySchema,
  CheckUniquenessFieldMappingQuerySchema,
  CheckUniquenessHierarchyQuerySchema,
  ContextMetadataRequestSchema,
  ContextMetadataResponseSchema,
  CreateFieldDefinitionRequestSchema,
  CreateFieldMappingRequestSchema,
  CreateFieldValueRequestSchema,
  CreateFieldValuesMappingRequestSchema,
  CreateHierarchyRequestSchema,
  DataCheckUniquenessSchema,
  DataFieldDefinitionSchema,
  DataFieldMappingSchema,
  DataMetadataEnumsSchema,
  DeleteFieldResponseSchema,
  DeleteFieldValuesMappingRequestSchema,
  EditFieldDefinitionRequestSchema,
  EditFieldMappingRequestSchema,
  EditFieldValueRequestSchema,
  EditHierarchyRequestSchema,
  FieldDefinitionCountChildDataSchema,
  FieldDefinitionOptionsResponseSchema,
  FieldDefinitionsCountChildRequestSchema,
  FieldDefinitionsRequestSchema,
  FieldMappingRequestSchema,
  FieldMappingsRequestSchema,
  FieldOptionsRequestSchema,
  FieldValue,
  FieldValuesOptionsRequestSchema,
  FieldValuesOptionsResponseSchema,
  FieldValuesRequestSchema,
  FieldValuesResponseSchema,
  HierarchiesRequestSchema,
  HierarchiesResponseSchema,
  HierarchyTreeRequestSchema,
  ManageHierarchyRequestSchema,
  ManageHierarchyResponseSchema,
  MdmPlatform,
  MultiFieldsFieldValuesOptionsRequestSchema,
  MultiFieldsFieldValuesOptionsResponseSchema,
  PlanningFormulaFieldMappingQuerySchema,
  PlanningFormulaFieldMappingResponseSchema,
} from './schemas/metadata.js';

export type MdmPlatformFieldValueType = MdmPlatform & {
  platform?: string;
  logo?: string;
  channels?: null;
};

export const getMetadataEnumsQueryOptions = defaultClient.query({
  url: 'mdm/meta/enums',
  method: 'GET',
  responseSchema: DataMetadataEnumsSchema,
  processResponse(data) {
    return data.data?.items?.[0] ?? null;
  },
});

export const checkUniquenessFieldDefinitionEndpoint = defaultClient.query({
  url: 'mdm/field-definition/check-uniqueness',
  method: 'GET',
  querySchema: CheckUniquenessFieldDefinitionQuerySchema,
  responseSchema: DataCheckUniquenessSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data?.items?.[0] ?? null;
  },
});

export const checkFieldUniquenessFieldMappingEndpoint =
  defaultApi.declareEndpoint({
    url: 'mdm/field-mapping/check-uniqueness',
    method: 'GET',
    querySchema: CheckUniquenessFieldMappingQuerySchema,
    responseSchema: DataCheckUniquenessSchema,
    clientOptions: {
      request: {
        snakeCaseQueryParams: true,
      },
    },
  });

export const fieldDefinitionsEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/',
  method: 'GET',
  querySchema: FieldDefinitionsRequestSchema,
  responseSchema: DataFieldDefinitionSchema,
  snakeCaseQueryParams: true,
});

export const fieldDefinitionOptionsEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/options',
  method: 'GET',
  responseSchema: FieldDefinitionOptionsResponseSchema,
  querySchema: FieldOptionsRequestSchema,
  snakeCaseQueryParams: true,
});

export const getFieldDefinitionOptionsQuery = makeQueryOptions(
  fieldDefinitionOptionsEndpoint,
  {
    processResponse(data) {
      return data.data;
    },
  },
);

export const fieldDefinitionEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/$fieldDefinitionId' as const,
  method: 'GET',
  responseSchema: DataFieldDefinitionSchema,
});

export const getFieldDefinitionQuery = makeQueryOptions(
  fieldDefinitionEndpoint,
  {
    processResponse(data) {
      return data.data?.items?.[0] ?? null;
    },
  },
);

export const getFieldDefinitionsQuery = makeQueryOptions(
  fieldDefinitionsEndpoint,
  {
    processResponse(data) {
      return data.data;
    },
  },
);

export const getGridFieldsQueryOptions = defaultClient.query({
  url: 'mdm/field-definition/count-child/',
  method: 'GET',
  querySchema: FieldDefinitionsCountChildRequestSchema,
  responseSchema: FieldDefinitionCountChildDataSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data;
  },
});

export const createFieldDefinitionEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/',
  method: 'POST',
  requestSchema: CreateFieldDefinitionRequestSchema,
  responseSchema: DataFieldDefinitionSchema,
});

export const editFieldDefinitionEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/$fieldDefinitionId' as const,
  method: 'PUT',
  requestSchema: EditFieldDefinitionRequestSchema,
  responseSchema: DataFieldDefinitionSchema,
});

export const deleteFieldDefinitionEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-definition/$fieldDefinitionId' as const,
  method: 'DELETE',
  responseSchema: DeleteFieldResponseSchema,
});

export const getFieldMappingQuery = defaultClient.query({
  url: 'mdm/field-mapping/',
  method: 'GET',
  responseSchema: DataFieldMappingSchema,
  snakeCaseQueryParams: true,
  querySchema: FieldMappingRequestSchema,
  processResponse(data) {
    return data.data;
  },
});

export const createFieldMappingEndpoint = defaultClient.query({
  url: 'mdm/field-mapping/',
  method: 'POST',
  requestSchema: CreateFieldMappingRequestSchema,
  responseSchema: DataFieldMappingSchema,
});

export const editFieldMappingEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/field-mapping/$fieldMappingId' as const,
  method: 'PUT',
  requestSchema: EditFieldMappingRequestSchema,
  responseSchema: DataFieldMappingSchema,
});

export const getFieldValuesOptionsQuery = defaultClient.query({
  url: 'mdm/master-data/options',
  method: 'GET',
  responseSchema: FieldValuesOptionsResponseSchema,
  querySchema: FieldValuesOptionsRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data;
  },
});

export const getFieldValuesQuery = defaultClient.query({
  url: 'mdm/master-data/',
  method: 'GET',
  responseSchema: FieldValuesResponseSchema,
  querySchema: FieldValuesRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data;
  },
});

export const multiFieldsValuesOptionsEndpoint = defaultClient.query({
  url: 'mdm/master-data/multi-fields',
  method: 'GET',
  responseSchema: MultiFieldsFieldValuesOptionsResponseSchema,
  querySchema: MultiFieldsFieldValuesOptionsRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return (
      data.data?.items?.reduce(
        (acc, item) => {
          acc[item.fieldDefinitionId] = item.options;
          return acc;
        },
        {} as Record<string, FieldValue[]>,
      ) ?? {}
    );
  },
});

export const getContextMetadata = defaultClient.query({
  url: 'mdm/context_metadata/',
  method: 'GET',
  responseSchema: ContextMetadataResponseSchema,
  querySchema: ContextMetadataRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data?.data?.items?.[0]?.contextMetadata;
  },
});

export const checkValueUniquenessEndpoint = defaultClient.query({
  url: 'mdm/master-data/check-uniqueness',
  method: 'GET',
  responseSchema: DataCheckUniquenessSchema,
  querySchema: CheckFieldValueUniquenessRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const createFieldValueEndpoint = defaultClient.query({
  url: 'mdm/master-data/',
  method: 'POST',
  requestSchema: CreateFieldValueRequestSchema,
  responseSchema: FieldValuesResponseSchema,
});

export const editFieldValueEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/master-data/$fieldValueId' as const,
  method: 'PUT',
  requestSchema: EditFieldValueRequestSchema,
  responseSchema: FieldValuesResponseSchema,
});

export const createFieldValueMappingsEndpoint = defaultClient.query({
  url: 'mdm/master-data-mapping/bulk',
  method: 'POST',
  requestSchema: CreateFieldValuesMappingRequestSchema,
  responseSchema: FieldValuesResponseSchema,
});

export const deleteFieldValueMappingsEndpoint = defaultClient.query({
  url: 'mdm/master-data-mapping/delete/bulk',
  method: 'POST',
  requestSchema: DeleteFieldValuesMappingRequestSchema,
  responseSchema: FieldValuesResponseSchema,
});

export const getFieldValueQuery = defaultClient.query({
  url: 'mdm/master-data/$fieldValueId' as const,
  method: 'GET',
  responseSchema: FieldValuesResponseSchema,
  processResponse(data) {
    return data.data?.items?.[0];
  },
});

export const getHierarchyTreeQuery = defaultClient.query({
  url: 'mdm/field-mapping/hierarchy-tree',
  method: 'GET',
  responseSchema: DataFieldMappingSchema,
  querySchema: HierarchyTreeRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data;
  },
});

export const createHierarchyEndpoint = defaultClient.query({
  url: 'mdm/hierarchy/',
  method: 'POST',
  requestSchema: CreateHierarchyRequestSchema,
  responseSchema: HierarchiesResponseSchema,
});
export const updateHierarchyEndpoint = defaultApi.declareEndpoint({
  url: 'mdm/hierarchy/$hierarchyId' as const,
  method: 'PUT',
  requestSchema: EditHierarchyRequestSchema,
  responseSchema: HierarchiesResponseSchema,
});

export const getHierarchiesQuery = defaultClient.query({
  url: 'mdm/hierarchy/',
  method: 'GET',
  responseSchema: HierarchiesResponseSchema,
  querySchema: HierarchiesRequestSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data;
  },
});

export const checkHierarchyUniquenessEndpoint = defaultClient.query({
  url: 'mdm/hierarchy/check-uniqueness',
  method: 'GET',
  responseSchema: DataCheckUniquenessSchema,
  querySchema: CheckUniquenessHierarchyQuerySchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const manageHierarchyEndpoint = defaultClient.query({
  url: 'mdm/field-definition/manage-hierarchy',
  method: 'POST',
  requestSchema: ManageHierarchyRequestSchema,
  responseSchema: ManageHierarchyResponseSchema,
});

export const fieldMappingBulkEndpoint = defaultClient.mutation({
  url: 'mdm/field-mapping/bulk',
  method: 'POST',
  requestSchema: FieldMappingsRequestSchema,
  responseSchema: DataFieldMappingSchema,
  processResponse: (data) => data,
});

export const planningFormulaFieldMappingEndpoint = defaultClient.query({
  url: 'mdm/field-mapping/planning-formula',
  method: 'GET',
  responseSchema: PlanningFormulaFieldMappingResponseSchema,
  querySchema: PlanningFormulaFieldMappingQuerySchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data;
  },
});

export const getContextMetadataQueryOptions = defaultClient.query({
  url: 'mdm/context_metadata/' as const,
  method: 'GET',
  querySchema: ContextMetadataRequestSchema,
  responseSchema: ContextMetadataResponseSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data.data.items[0].contextMetadata;
  },
});

export const getRelatedFieldsMapQueryOptions = defaultClient.query({
  url: 'mdm/context_metadata/build-related-fields-map',
  method: 'POST',
  requestSchema: BuildRelatedFieldsMapRequestSchema,
  responseSchema: BuildRelatedFieldsMapResponseSchema,
  processResponse(data) {
    return data.data.items[0].relatedFields;
  },
});
